namespace $ {

	export type $shm_agent_message_chart_spec = {
		version: 1
		height?: number
		option: Record< string, any >
	}

	export type $shm_agent_message_chart_result = {
		spec: $shm_agent_message_chart_spec | null
		error: string
	}

	export function $shm_agent_message_chart_instruction() {
		return [
			'Когда данные лучше понять визуально, можешь встроить интерактивный ECharts-график в ответ.',
			'Перед графиком дай короткий текстовый вывод. Сам график верни отдельным fenced-блоком языка echarts строго в таком JSON-формате:',
			'```echarts',
			'{"version":1,"height":320,"option":{"tooltip":{"trigger":"axis"},"xAxis":{"type":"category","data":["Пн","Вт"]},"yAxis":{"type":"value","name":"Упоминания"},"series":[{"type":"line","name":"Упоминания","data":[12,18]}]}}',
			'```',
			'Внутри блока допустим только JSON: без JavaScript-функций, HTML, formatter, renderItem, graphic, toolbox и внешних URL/data/blob/image ресурсов.',
			'Подписывай оси, единицы измерения и серии. Не помещай обычный текст внутрь echarts-блока.',
		].join( '\n' )
	}

	const $shm_agent_message_chart_series = new Set([
		'bar',
		'boxplot',
		'candlestick',
		'effectScatter',
		'funnel',
		'gauge',
		'graph',
		'heatmap',
		'line',
		'lines',
		'parallel',
		'pie',
		'pictorialBar',
		'radar',
		'sankey',
		'scatter',
		'sunburst',
		'themeRiver',
		'tree',
		'treemap',
	])

	function $shm_agent_message_chart_plain( value: any ) {
		return Boolean( value ) && typeof value === 'object' && !Array.isArray( value )
	}

	function $shm_agent_message_chart_safe( value: any, state: { nodes: number }, depth = 0 ): any {
		if( ++ state.nodes > 20_000 ) throw new Error( 'В графике слишком много данных' )
		if( depth > 16 ) throw new Error( 'График имеет слишком глубокую структуру' )

		if( typeof value === 'string' ) {
			if( value.length > 20_000 ) throw new Error( 'В графике найдена слишком длинная строка' )
			if( /<\/?[a-z][^>]*>/i.test( value ) ) throw new Error( 'HTML в графиках запрещён' )
			if( /(?:data:|blob:|https?:\/\/|image:\/\/)/i.test( value ) ) {
				throw new Error( 'Внешние ресурсы в графиках запрещены' )
			}
			return value
		}
		if( value === null || [ 'number', 'boolean' ].includes( typeof value ) ) return value
		if( Array.isArray( value ) ) {
			if( value.length > 5_000 ) throw new Error( 'В массиве графика слишком много элементов' )
			return value.map( item => $shm_agent_message_chart_safe( item, state, depth + 1 ) )
		}
		if( !$shm_agent_message_chart_plain( value ) ) throw new Error( 'Недопустимое значение в графике' )

		const result = {} as Record< string, any >
		for( const [ key, item ] of Object.entries( value ) ) {
			if( [ '__proto__', 'constructor', 'prototype' ].includes( key ) ) throw new Error( `Недопустимое поле: ${ key }` )
			if( key === 'formatter' || key === 'renderItem' ) continue
			if( key === 'graphic' || key === 'toolbox' ) continue
			result[ key ] = $shm_agent_message_chart_safe( item, state, depth + 1 )
		}
		return result
	}

	export function $shm_agent_message_chart_parse( source: string ): $shm_agent_message_chart_result {
		if( source.length > 250_000 ) return { spec: null, error: 'Описание графика слишком большое' }

		const decoded = $mol_error_fence(
			()=> ({ value: JSON.parse( source ), error: '' }),
			error => ({ value: null, error: error.message }),
		)
		if( decoded.error ) return { spec: null, error: `Некорректный JSON: ${ decoded.error }` }
		const parsed = decoded.value
		if( !$shm_agent_message_chart_plain( parsed ) ) return { spec: null, error: 'Ожидался JSON-объект графика' }
		if( parsed.version !== 1 ) return { spec: null, error: 'Поддерживается только version: 1' }
		if( !$shm_agent_message_chart_plain( parsed.option ) ) return { spec: null, error: 'Поле option должно содержать объект ECharts' }

		const series = parsed.option.series === undefined
			? []
			: Array.isArray( parsed.option.series ) ? parsed.option.series : [ parsed.option.series ]
		if( series.length > 20 ) return { spec: null, error: 'В графике может быть не больше 20 серий' }
		for( const item of series ) {
			if( !$shm_agent_message_chart_plain( item ) ) return { spec: null, error: 'Каждая серия должна быть объектом' }
			if( typeof item.type !== 'string' || !$shm_agent_message_chart_series.has( item.type ) ) {
				return { spec: null, error: `Неподдерживаемый тип серии: ${ String( item.type || 'не указан' ) }` }
			}
		}

		const height = parsed.height === undefined ? 320 : Number( parsed.height )
		if( !Number.isFinite( height ) ) return { spec: null, error: 'Высота графика должна быть числом' }

		const secured = $mol_error_fence(
			()=> ({ value: $shm_agent_message_chart_safe( parsed.option, { nodes: 0 } ), error: '' }),
			error => ({ value: null, error: error.message }),
		)
		if( secured.error ) return { spec: null, error: secured.error }
		const sanitized = secured.value

		const tooltip = $shm_agent_message_chart_plain( sanitized.tooltip ) ? sanitized.tooltip : {}
		sanitized.tooltip = {
			... tooltip,
			renderMode: 'richText',
			appendToBody: false,
		}

		return {
			spec: {
				version: 1,
				height: Math.max( 240, Math.min( 600, Math.round( height ) ) ),
				option: sanitized,
			},
			error: '',
		}
	}

}
